import React, { useState, useEffect, useRef } from "react";
import {
  Box,
  Typography,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  IconButton,
  CircularProgress,
  Pagination,
  InputBase,
  Menu,
  MenuItem,
  Button
} from "@mui/material";
import MoreVertIcon from "@mui/icons-material/MoreVert";
import SearchIcon from "@mui/icons-material/Search";
import { useSelector } from "react-redux";
import { decryptToken } from "../../../../Utlis/Encryption";
import { BASE_URL } from "../../../../components/BaseUrl";
import CustomText, { textStyles } from "../../../../components/CustomText";
import empty from "../../../../assets/empty.png";
import EditScheduleModal from "./EditModal";
import { useTranslation } from "react-i18next";
import AddScheduleDrawer from "./AddScheduleDrawer";
import AddIcon from "@mui/icons-material/Add";
import { useTheme } from "../../../../contexts/ThemeContext";
import { convertUTCToLocalDateTime } from "../../../../components/dateTimeUtils";

const formatWorkingHoursDisplay = (dailyHours) => {
  if (dailyHours === null || dailyHours === undefined || dailyHours === "" || dailyHours === "-") {
    return "-";
  }
  const hours = Number(dailyHours);
  if (Number.isNaN(hours)) return dailyHours;

  const totalMinutes = Math.max(0, Math.round(hours * 60));
  const h = Math.floor(totalMinutes / 60);
  const m = totalMinutes % 60;

  if (h > 0 && m > 0) return `${h}h ${m}mins`;
  if (h > 0) return `${h}h`;
  return `${m}mins`;
};

const AttendanceSettings = ({worker}) => {

  const [workers, setWorkers] = useState([]);
const [loading, setLoading] = useState(false);        // for search/pagination
const [initialLoading, setInitialLoading] = useState(true); 
  const [error, setError] = useState(null);
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [searchTerm, setSearchTerm] = useState("");
const { t } = useTranslation();
  const token = useSelector((state) => state.auth.token);
  const decryptedToken = token ? decryptToken(token) : null;
  const { primaryColor, secondaryColor, primaryTextColor, secondaryTextColor } = useTheme();
const itemsPerPage = 5;
const [isEditModalOpen, setIsEditModalOpen] = useState(false);
  const [drawerOpen, setDrawerOpen] = useState(false);
  // Menu handling
  const [anchorEl, setAnchorEl] = useState(null);
  const [menuRow, setMenuRow] = useState(null);

const handleMenuOpen = (event, row) => {
  setAnchorEl(event.currentTarget);
  setMenuRow(row);   // ✅ the worker row is saved here
};
  const handleMenuClose = () => {
    setAnchorEl(null);
    // setMenuRow(null);
  };

  const isFirstWorkersFetch = useRef(true);

const fetchWorkers = async (page = 1, size = itemsPerPage, isInitial = false) => {
  if (isInitial) {
    setInitialLoading(true);
  } else {
    setLoading(true);
  }

  try {
    const params = new URLSearchParams({
      expand: "attendanceSettings",
      page: String(page),
      limit: String(size),
    });
    const trimmed = searchTerm.trim();
    if (trimmed) {
      params.set("search", trimmed);
    }
    const url = `${BASE_URL}/company-admins/workers?${params.toString()}`;

    const res = await fetch(url, {
      method: "GET",
      headers: {
        "Content-Type": "application/json",
        ...(decryptedToken ? { Authorization: `Bearer ${decryptedToken}` } : {}),
      },
    });
console.log("🔹 [AttendanceSettings] company-admins/workers response:", res);
    if (!res.ok) throw new Error(`Request failed: ${res.status}`);
    const json = await res.json();
    console.log("[AttendanceSettings] company-admins/workers full response:", json);
    const browserTimezone = Intl.DateTimeFormat().resolvedOptions().timeZone;
    console.log("🌍 [LIST] Browser timezone used for display:", browserTimezone);

    const workerRows = json?.data?.workers?.map((w) => ({
      id: w.id,
      name: `${w.first_name || ""} ${w.last_name || ""}`.trim(),
      email: w.email || "-",
      shiftStart: (() => {
        const utcValue = w.attendanceSettings?.schedule?.startTime || "-";
        const localValue = convertUTCToLocalDateTime(utcValue, {
          formatOptions: { hour: "numeric", minute: "2-digit", hour12: true },
        });
        console.log("🕒 [LIST][UTC->LOCAL] Shift start display conversion", {
          workerId: w.id,
          utcFromApi: utcValue,
          timezoneUsed: browserTimezone,
          localDisplay: localValue,
        });
        return localValue;
      })(),
      shiftEnd: (() => {
        const utcValue = w.attendanceSettings?.schedule?.endTime || "-";
        const localValue = convertUTCToLocalDateTime(utcValue, {
          formatOptions: { hour: "numeric", minute: "2-digit", hour12: true },
        });
        console.log("🕒 [LIST][UTC->LOCAL] Shift end display conversion", {
          workerId: w.id,
          utcFromApi: utcValue,
          timezoneUsed: browserTimezone,
          localDisplay: localValue,
        });
        return localValue;
      })(),
      workingHours: formatWorkingHoursDisplay(
        w.attendanceSettings?.schedule?.dailyHours
      ),
      status: w.status || "-",
      __raw: w,
    }));

    setWorkers(workerRows);
    setTotalPages(json?.data?.pagination?.pages || 1);
  } catch (err) {
    console.error(err);
    setError(err.message);
    setWorkers([]);
  } finally {
    setLoading(false);
    setInitialLoading(false);
  }
};
  useEffect(() => {
    const isInitial = isFirstWorkersFetch.current;
    isFirstWorkersFetch.current = false;
    fetchWorkers(currentPage, itemsPerPage, isInitial);
  }, [currentPage, searchTerm]);

  const HeaderStyles = {
    color: "#44546F",
    fontSize: "14px",
    fontFamily: "Poppins, sans-serif",
    fontWeight: 600,
    textAlign: "center",
    whiteSpace: "nowrap",
  };

  const DataStyles = {
    color: "#172B4D",
    fontSize: "13px",
    fontFamily: "Poppins, sans-serif",
    fontWeight: 400,
    whiteSpace: "nowrap",
    textAlign: "center",
  };
  return (
    <Box
      sx={{
        backgroundColor: "white",
        borderRadius: 2,
        border: "2px solid #091E4224",
        py: 2,
        ml: 1,
        mr: 1,
        mt: 1,
      }}
    >
      {/* Header */}
      <Box
        sx={{
          display: "flex",
          alignItems: "center",
          flexWrap:"wrap",
          justifyContent: "space-between",
          pb: 2,
          px: 2,
          borderBottom: "2px solid #091E4224",
          gap: 2,
        }}
      >
        {/* <CustomText
          sx={{
            ...textStyles.h1,
            fontWeight: 600,
            fontSize: "18px",
            color: "#003149",
          }}
        >
          Attendance Settings
        </CustomText> */}
<Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
  <CustomText
    sx={{
      ...textStyles.h1,
      fontWeight: 600,
      fontSize: "18px",
      color: "#003149",
    }}
  >
     {t("attendanceSettings.heading")}
  </CustomText>
  {loading && !initialLoading && (
    <CircularProgress size={18} sx={{ color: primaryColor }} />
  )}
</Box>
        {/* Search */}
        <Box
          sx={{
            px: 1,
            py: 0.2,
            borderRadius: 1,
            height: 34,
            border: "2px solid #091E4224",
            display: "flex",
            alignItems: "center",
            minWidth: 250,
            maxWidth: 300,
            width: "100%",
          }}
        >
          <InputBase
            placeholder={t("attendanceSettings.searchPlaceholder")}
            value={searchTerm}
            onChange={(e) => {
              setSearchTerm(e.target.value);
              setCurrentPage(1); // reset page on new search
            }}
            sx={{
              ml: 0.5,
              fontSize: 15,
              fontFamily: "Poppins, sans-serif",
              color: "#939393",
              fontWeight: 400,
              width: "100%",
            }}
          />
          <SearchIcon sx={{ fontSize: 22, color: "#888", mr: 0.5 }} />
        </Box>
            <Button
                      onClick={() => setDrawerOpen(true)}
                      variant="contained"
                      size="small"
                      sx={{
                        px: 2,
                        backgroundColor: primaryColor,
                        color: primaryTextColor,
                        height: 32,
                        fontSize: 14,
                        fontWeight: 400,
                        fontFamily: "Poppins, sans-serif",
                        borderRadius: "4px",
                        textTransform: "none",
                        transition: "all 0.3s ease",
                        "&:hover": {
                          backgroundColor: secondaryColor,
                          color: secondaryTextColor,
                        },
                      }}
                      startIcon={<AddIcon sx={{ fontSize: 18 }} />}
                    >
                      {t("common.add")}
                    </Button>
      </Box>

      {/* Table */}
      <TableContainer component={Paper} sx={{ boxShadow: "none" }}>
      {initialLoading ? (
          <Box sx={{ display: "flex", justifyContent: "center", height: 300, alignItems: "center" }}>
            <CircularProgress sx={{ color: primaryColor }} />
          </Box>
        ) : (
          <Table>
            <TableHead sx={{ borderTop: "2px solid #091E4224", borderBottom: "2px solid #091E4224" }}>
              <TableRow>
                <TableCell sx={HeaderStyles}>{t("attendanceSettings.columns.id")}</TableCell>
                <TableCell sx={HeaderStyles}>{t("attendanceSettings.columns.name")}</TableCell>
                <TableCell sx={HeaderStyles}>{t("attendanceSettings.columns.email")}</TableCell>
                <TableCell sx={HeaderStyles}>{t("attendanceSettings.columns.shiftStart")}</TableCell>
                <TableCell sx={HeaderStyles}>{t("attendanceSettings.columns.shiftEnd")}</TableCell>
                {/* <TableCell sx={HeaderStyles}>{t("attendanceSettings.columns.workingHours")}</TableCell> */}
                <TableCell sx={HeaderStyles}>{t("attendanceSettings.columns.action")}</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
             {workers.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={7}>
                    <Box
                      sx={{
                        display: "flex",
                        flexDirection: "column",
                        alignItems: "center",
                        py: 5,
                      }}
                    >
                      <img src={empty} alt="No Data" style={{ width: 200, marginBottom: 16 }} />
                      <Typography sx={{fontSize:"15px"}}>{t("empty.no_user")}</Typography>
                    </Box>
                  </TableCell>
                </TableRow>
              ) : (
                workers.map((row, i) => (
                  <TableRow key={`${row.id}-${i}`}>
                    <TableCell sx={DataStyles}>{row.id}</TableCell>
                    <TableCell sx={DataStyles}>{row.name}</TableCell>
                    <TableCell sx={DataStyles}>{row.email}</TableCell>
                    <TableCell sx={DataStyles}>{row.shiftStart}</TableCell>
                    <TableCell sx={DataStyles}>{row.shiftEnd}</TableCell>
                    {/* <TableCell sx={DataStyles}>{row.workingHours}</TableCell> */}
                    <TableCell sx={{ textAlign: "center" }}>
                      <IconButton size="small" onClick={(e) => handleMenuOpen(e, row)}>
                        <MoreVertIcon fontSize="small" />
                      </IconButton>
                    </TableCell>
                  </TableRow>
                ))
              )}
            </TableBody>
          </Table>
        )}
      </TableContainer>

      {/* Action Menu */}
      <Menu
        anchorEl={anchorEl}
        open={Boolean(anchorEl)}
        onClose={handleMenuClose}
        anchorOrigin={{ vertical: "bottom", horizontal: "center" }}
        transformOrigin={{ vertical: "top", horizontal: "center" }}
      >
     
        <MenuItem
  onClick={() => {
    setIsEditModalOpen(true);
    // menuRow is already set in handleMenuOpen when clicking the icon
    console.log("🔹 Editing worker:", menuRow);
    handleMenuClose();
  }}
  sx={{ fontSize: "13px" }}
>
   {t("common.edit")}
</MenuItem>
      </Menu>

      {/* Pagination */}
      <Box sx={{ display: "flex", justifyContent: "center", mt: 2 }}>
        <Pagination
          count={totalPages}
          page={currentPage}
          onChange={(e, value) => setCurrentPage(value)}
          shape="rounded"
          color="primary"
          size="small"
          sx={{
            "& .MuiPaginationItem-root": {
              fontFamily: "Poppins, sans-serif",
              fontSize: 13,
              fontWeight: 500,
            },
            "& .Mui-selected": {
              backgroundColor: `${primaryColor}10`,
              color: primaryColor,
            },
          }}
        />
      </Box>
<EditScheduleModal
  open={isEditModalOpen}
  onClose={() => {
    setIsEditModalOpen(false);
    setMenuRow(null);   // ✅ clear here instead
  }}
  worker={menuRow}
  onUpdateSuccess={() => fetchWorkers(currentPage, itemsPerPage, false)}
/>
    <AddScheduleDrawer
        open={drawerOpen}
        onClose={() => setDrawerOpen(false)}
        worker={worker}
        onAddSuccess={() => fetchWorkers(currentPage, itemsPerPage, false)}
      />
    </Box>
  );
};

export default AttendanceSettings;
